import { Helmet } from 'react-helmet-async';
import InfoTooltip from '../components/InfoTooltip';
import { useNavigate } from 'react-router-dom';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { MdOutlineEdit } from 'react-icons/md';
import { CiTrash } from 'react-icons/ci';
import {
  Button,
  Card,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  CircularProgress,
} from '@mui/material';
import Swal from 'sweetalert2';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import {
  deleteContractTemplates,
  getAllContractTemplates,
  setDefaultContractTemplate,
} from '../helper/backend_helper';

const TABLE_HEAD = ['Name', 'Mode', 'Default', 'Last Used', 'Usage', 'Updated', 'Actions'];

const modeLabel = (mode) => (mode === 'allInclusive' ? 'All Inclusive' : 'Standard');

const formatDateTime = (value) => {
  if (!value) return 'Never used';
  return new Date(value).toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const sortByUsage = (items) => [...items].sort((a, b) => {
  const bTime = new Date(b.lastUsedAt || b.updatedAt || b.createdAt || 0).getTime();
  const aTime = new Date(a.lastUsedAt || a.updatedAt || a.createdAt || 0).getTime();
  return bTime - aTime;
});

export default function ContractTemplates() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  async function fetchData() {
    try {
      setLoading(true);
      const response = await getAllContractTemplates();
      setTemplates(response?.data?.data || []);
    } catch (error) {
      console.error('Error fetching contract templates:', error);
      Swal.fire({ icon: 'error', title: 'Could not load contract templates' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, []);

  const groupedTemplates = useMemo(() => ({
    standard: sortByUsage(templates.filter((template) => (template.mode || 'standard') === 'standard')),
    allInclusive: sortByUsage(templates.filter((template) => template.mode === 'allInclusive')),
  }), [templates]);

  const visibleRows = useMemo(() => [
    { section: 'Standard', rows: groupedTemplates.standard },
    { section: 'All Inclusive', rows: groupedTemplates.allInclusive },
  ], [groupedTemplates]);

  const handleSetDefault = async (template) => {
    try {
      await setDefaultContractTemplate(template._id, { headers: { 'Content-Type': 'application/json' } });
      Swal.fire({ icon: 'success', title: `${template.name} is now the default ${modeLabel(template.mode)} template` });
      fetchData();
    } catch (error) {
      console.error('Error setting default template:', error);
      Swal.fire({ icon: 'error', title: 'Could not set default template' });
    }
  };

  const handleDelete = async (_id) => {
    try {
      const confirmed = await Swal.fire({
        icon: 'warning',
        title: 'Are you sure you want to delete this Template?',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'No, cancel!',
      });
      if (confirmed.isConfirmed) {
        await deleteContractTemplates(_id, { headers: { 'Content-Type': 'application/json' } });
        Swal.fire({ icon: 'success', title: 'Template deleted successfully' });
        fetchData();
      }
    } catch (error) {
      console.error(error);
      Swal.fire({ icon: 'error', title: 'An error occurred while deleting' });
    }
  };

  return (
    <>
      <Helmet><title> Contract Templates </title></Helmet>
      <Card sx={{ p: 3 }}>
        <Stack mb={3} direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h4">
            Contract Templates{' '}
            <InfoTooltip
              width={20}
              title="Reusable contract layouts used to generate owner agreements, grouped into Standard and All Inclusive modes. One template per mode can be marked as the default, which is applied automatically when a new contract is created. Use Add Template to create one, Set default to choose the active template, and the usage and last-used columns to see which are in use."
            />
          </Typography>
          <Button
            sx={{ boxShadow: 'none', backgroundColor: '#1E3A5F', '&:hover': { backgroundColor: '#1E3A5F', boxShadow: 'none' } }}
            variant="contained"
            endIcon={<Iconify icon="eva:plus-fill" />}
            onClick={() => navigate('/contractTemplates/add_contractTemplate')}
          >
            Add Template
          </Button>
        </Stack>

        <Scrollbar>
          <TableContainer sx={{ minWidth: 900 }}>
            <Table>
              <TableHead>
                <TableRow>
                  {TABLE_HEAD.map((label) => <TableCell key={label} align={label === 'Actions' ? 'center' : 'left'}>{label}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={TABLE_HEAD.length}><CircularProgress size="20px" /></TableCell></TableRow>
                )}
                {!loading && visibleRows.map(({ section, rows }) => (
                  <Fragment key={section}>
                    <TableRow>
                      <TableCell colSpan={TABLE_HEAD.length} sx={{ bgcolor: '#F7F8F7', fontWeight: 700 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="subtitle1">{section}</Typography>
                          <Chip size="small" label={`${rows.length} templates`} />
                        </Stack>
                      </TableCell>
                    </TableRow>
                    {rows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={TABLE_HEAD.length}>
                          <Typography variant="body2" color="text.secondary">No {section.toLowerCase()} templates yet.</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {rows.map((row) => (
                      <TableRow hover key={row._id}>
                        <TableCell><Typography variant="subtitle2" noWrap>{row.name}</Typography></TableCell>
                        <TableCell><Chip size="small" label={modeLabel(row.mode)} color={row.mode === 'allInclusive' ? 'success' : 'default'} /></TableCell>
                        <TableCell>
                          {row.isDefault ? <Chip size="small" color="primary" label="Default" /> : (
                            <Button size="small" variant="outlined" onClick={() => handleSetDefault(row)}>Set default</Button>
                          )}
                        </TableCell>
                        <TableCell>{formatDateTime(row.lastUsedAt)}</TableCell>
                        <TableCell>{row.usageCount || 0}</TableCell>
                        <TableCell>{formatDateTime(row.updatedAt)}</TableCell>
                        <TableCell align="center">
                          <Stack direction="row" justifyContent="center" alignItems="center" gap={1}>
                            <Button
                              onClick={() => navigate('/contractTemplates/update_contractTemplate', { state: { userData: row } })}
                              sx={{ boxShadow: 'none', backgroundColor: '#B5FFD8', color: '#009245', '&:hover': { backgroundColor: '#B5FFD8', boxShadow: 'none' } }}
                              size="small"
                              variant="contained"
                              startIcon={<MdOutlineEdit />}
                            >
                              Edit
                            </Button>
                            <Button
                              onClick={() => handleDelete(row._id)}
                              sx={{ boxShadow: 'none', backgroundColor: '#FFC6C6', color: '#DC2626', '&:hover': { backgroundColor: '#FFC6C6', boxShadow: 'none' } }}
                              size="small"
                              variant="contained"
                              startIcon={<CiTrash />}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </Fragment>
                ))}
                {!loading && templates.length === 0 && (
                  <TableRow>
                    <TableCell align="center" colSpan={TABLE_HEAD.length} sx={{ py: 3 }}>
                      <Paper sx={{ textAlign: 'center', p: 3 }}>
                        <Typography variant="h6" paragraph>No templates found</Typography>
                        <Typography variant="body2">Create a standard or all-inclusive template to start using contract defaults.</Typography>
                      </Paper>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>

        <TablePagination
          rowsPerPageOptions={[100, 200, 300]}
          component="div"
          count={templates.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => { setPage(0); setRowsPerPage(parseInt(event.target.value, 10)); }}
        />
      </Card>
    </>
  );
}
